import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const isFetchBaseQueryError = (
  error: unknown,
): error is FetchBaseQueryError => {
  return typeof error === 'object' && error != null && 'status' in error;
};

export type ConversationSummary = {
  /** The id of the summary. */
  readonly id: string;
  /** The organization id. */
  readonly organization_id: string;
  /** The conversation id. */
  readonly csid: string;
  /** The summary of the conversation messages. */
  readonly summary: string;
  /** The timestamp of when the summary was generated. */
  readonly created_at: string;
  /**
   * The id of the last message used in summary generation. Useful for knowing
   * if there are new messages after the summary is generated.
   */
  readonly last_used_message_id: string;
  /**
   * The feedback id used for submitting feedback back to the LLM service.
   *
   * @remarks
   *   The `null` value should almost never occur, but if it does, then it can be
   *   used as a guard.
   */
  readonly feedback_id: string | null;

  readonly language?: string;
};

export type MessageError = FetchBaseQueryError & {
  data: {
    message: string;
  };
};

/**
 * Type predicate to narrow an unknown error to an object with the property
 * 'data' containing a string 'message' property
 */
export const isMessageError = (error: unknown): error is MessageError => {
  if (isFetchBaseQueryError(error)) {
    const errorData = (error as MessageError).data;
    return (
      typeof errorData === 'object' &&
      errorData != null &&
      'message' in errorData &&
      typeof errorData.message === 'string'
    );
  }
  return false;
};
